import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  type ComboboxProps,
} from "@headlessui/react";
import { TournamentEntry } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";

interface Props {
  autoFocus?: boolean;
  onSelect(entry: TournamentEntry): void;
  entries: TournamentEntry[];
  value: TournamentEntry | null;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

interface State {
  entries: TournamentEntry[];
  filteredEntries: TournamentEntry[];
  search: string;
  onSelect(entry: TournamentEntry): void;
}

class EntrySearchController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      entries: [],
      filteredEntries: [],
      search: "",
      onSelect: () => {},
    };
  }

  async initialize(props: Props) {
    this.changeProps(props);
  }

  async changeProps(props: Props) {
    if (!props.entries) throw new Error("entries is required");
    this.state.entries = props.entries;
    this.state.onSelect = props.onSelect;
  }

  actionSetSearch(search: string) {
    this.state.search = search;

    if (search.length < 1) {
      this.state.filteredEntries = this.state.entries;
      return;
    }

    this.state.filteredEntries = this.state.entries.filter((entry) => {
      const name = entry.name || "";
      return name.toLowerCase().includes(search.toLowerCase());
    });
  }

  actionSelect(entry: TournamentEntry) {
    this.state.onSelect(entry);
    this.actionSetSearch("");
  }
}

const TournamentEntrySearch: React.FC<Props> = ({ value, autoFocus, error, disabled, placeholder }) => {
  const controller = EntrySearchController.use();
  const { filteredEntries } = controller.state;

  const options = filteredEntries.map((entry, idx) => {
    return (
      <ComboboxOption
        key={entry.id}
        value={entry}
        // biome-ignore lint/a11y/useValidAriaValues: old
        aria-roledescription=""
        className={clsx(
          "border-l border-r border-slate-300 p-4 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black",
          { "rounded-b-lg border-b": idx === filteredEntries.length - 1 },
        )}
      >
        <div className="flex items-center justify-between">
          <span>{entry.name || "Unnamed entry"}</span>
        </div>
      </ComboboxOption>
    );
  });

  // Trick to make the combobox accept the union type
  const comboboxProps: ComboboxProps<typeof value, false> = {
    value: value,
    disabled,
  };

  return (
    <Combobox
      {...comboboxProps}
      onChange={(v) => {
        if (v instanceof TournamentEntry) {
          controller.actionSelect(v);
        }
      }}
    >
      <div className="relative w-full">
        <ComboboxInput
          title="Search for an entry"
          data-testid="entry-search-input"
          data-1p-ignore
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          placeholder={placeholder || "Search entry"}
          className={clsx(
            "mt-1 block w-full rounded border p-2",
            error ? "border-red-500" : "border-gray-300",
          )}
          onChange={(event) => controller.actionSetSearch(event.target.value)}
          displayValue={(_value: TournamentEntry) => ""}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center pl-3">
          <svg
            aria-hidden="true"
            className="h-5 w-5 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
      <ComboboxOptions
        anchor="bottom start"
        className="relative z-50"
        style={{ width: "var(--input-width)" }}
      >
        {options}
      </ComboboxOptions>
    </Combobox>
  );
};

const EntrySearchScoped = EntrySearchController.scope(ApplicationView(TournamentEntrySearch));
export { EntrySearchScoped as TournamentEntrySearch };
